import { Component, OnInit } from '@angular/core';
import { DataService } from '../data.service';
import { ValidationService } from '../validation.service';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { ShellStep } from '../shared/ip-shell/ip-shell.component';

@Component({
  selector: 'app-cashflowstep4',
  templateUrl: './cashflowstep4.component.html',
  styleUrls: ['./cashflowstep4.component.css']
})
export class Cashflowstep4Component implements OnInit {
  readonly cfSteps: ShellStep[] = [
    { num: 1, label: 'Step 01', title: 'Location & Buyer' },
    { num: 2, label: 'Step 02', title: 'Property & Financing' },
    { num: 3, label: 'Step 03', title: 'Rental Income' },
    { num: 4, label: 'Step 04', title: 'Costs & Expenses' },
  ];

  foreignbuyer: string = '0';
  stampDutyLandTax: string = '';
  loanOriginationFee: string = '';
  calcData: any;
  country: string = '';
  PropertyValue: string = '';
  letteingManagFee: string = '';
  groundRent: string = '';
  serviceCharges: string = '';
  miscelleneousExpense: string = '';
  PropertyLondon: any;

  constructor(
    private router: Router,
    private dataService: DataService,
    public validation: ValidationService
  ) {
    this.calcData = JSON.parse(localStorage.getItem('calcData') || '{}') || {};

    if (!this.dataService.EmptyNullOrUndefined(this.calcData.foreignbuyer)) {
      this.foreignbuyer = this.calcData.foreignbuyer;
    } else if (!this.dataService.EmptyNullOrUndefined(this.calcData.ForeignBuyer)) {
      this.foreignbuyer = this.calcData.ForeignBuyer;
    }
    if (!this.dataService.EmptyNullOrUndefined(this.calcData.stampDutyLandTax)) {
      this.stampDutyLandTax = this.validation.amountWithComma(String(this.calcData.stampDutyLandTax));
    }
    if (!this.dataService.EmptyNullOrUndefined(this.calcData.loanOriginationFee)) {
      this.loanOriginationFee = this.calcData.loanOriginationFee + '%';
    }
    if (this.calcData.optmortgage === '0') {
      this.loanOriginationFee = '0%';
    }
    if (!this.dataService.EmptyNullOrUndefined(this.calcData.Country)) {
      this.country = this.calcData.Country;
    }
    if (!this.dataService.EmptyNullOrUndefined(this.calcData.PropertyValue)) {
      this.PropertyValue = this.calcData.PropertyValue;
    }
    if (!this.dataService.EmptyNullOrUndefined(this.calcData.letteingManagFee)) {
      this.letteingManagFee = this.calcData.letteingManagFee + '%';
    }
    if (!this.dataService.EmptyNullOrUndefined(this.calcData.groundRent)) {
      this.groundRent = this.validation.amountWithComma(String(this.calcData.groundRent));
    }
    if (!this.dataService.EmptyNullOrUndefined(this.calcData.serviceCharges)) {
      this.serviceCharges = this.validation.amountWithComma(String(this.calcData.serviceCharges));
    }
    if (!this.dataService.EmptyNullOrUndefined(this.calcData.miscelleneousExpense)) {
      this.miscelleneousExpense = this.validation.amountWithComma(String(this.calcData.miscelleneousExpense));
    }
  }

  ngOnInit(): void {
    this.PropertyLondon = localStorage.getItem('PropertyLondon');

    // Pre-fill defaults based on location
    if (!this.loanOriginationFee && this.calcData.optmortgage !== '0') this.loanOriginationFee = '1%';
    if (!this.letteingManagFee) this.letteingManagFee = this.PropertyLondon == '1' ? '12%' : '10%';
    if (!this.groundRent) this.groundRent = '0';
    if (!this.serviceCharges) this.serviceCharges = this.autoServiceCharge();
    if (!this.miscelleneousExpense) this.miscelleneousExpense = '1,000';

    if (!this.stampDutyLandTax && this.PropertyValue) {
      this.calculatestampduty();
    }
  }

  calculatestampduty() {
    const apiCountry = this.resolveApiCountry(this.country);
    const pv = parseFloat(this.PropertyValue.toString().replace(/,/g, '')) || 0;
    if (!pv) return;

    const isForeignBuyer = this.foreignbuyer === '1';
    const buyerType = this.calcData.BuyerType || 'investment';
    const apiMode = buyerType === 'ftb' ? 'first_time' : 'investment';
    this.dataService.GetRequest(
      'https://api.propertydata.co.uk/stamp-duty-calculator?key=' + environment.PropertyDataKey +
      '&value=' + pv + '&country=' + apiCountry + '&mode=' + apiMode
    ).subscribe(
      (res: any) => {
        const obj = JSON.parse(JSON.stringify(res));
        if (obj.status === 'success') {
          let sdlt = parseFloat(obj.transaction_tax_payable);
          // API handles base rates; add 2% non-resident surcharge manually
          if (isForeignBuyer) { sdlt += pv * 0.02; }
          this.stampDutyLandTax = this.validation.amountWithComma(Math.round(sdlt).toString());
        } else {
          this.stampDutyLandTax = this.validation.amountWithComma(
            this.calculateSdltFallback(pv, isForeignBuyer, buyerType).toString()
          );
        }
      },
      (_err: any) => {
        const pv2 = parseFloat(this.PropertyValue.toString().replace(/,/g, '')) || 0;
        this.stampDutyLandTax = this.validation.amountWithComma(
          this.calculateSdltFallback(pv2, isForeignBuyer, buyerType).toString()
        );
      }
    );
  }

  resolveApiCountry(raw: string): string {
    if (!raw) return 'england';
    const r = raw.toLowerCase().trim();
    if (r === 'scotland') return 'scotland';
    if (r === 'wales') return 'wales';
    if (r === 'northern-ireland' || r === 'northern ireland') return 'northern-ireland';
    return 'england';
  }

  calculateSdltFallback(value: number, isForeignBuyer: boolean, buyerType: string = 'investment'): number {
    let sdlt = 0;
    if (buyerType === 'ftb' && value <= 500000) {
      // FTB relief: 0% up to £300k, 5% on £300k–£500k
      if (value > 300000) sdlt = (value - 300000) * 0.05;
    } else if (buyerType === 'ftb') {
      // FTB > £500k loses relief — standard bands
      if (value > 125000) sdlt += Math.min(value - 125000, 125000) * 0.02;
      if (value > 250000) sdlt += Math.min(value - 250000, 675000) * 0.05;
      if (value > 925000) sdlt += Math.min(value - 925000, 575000) * 0.10;
      if (value > 1500000) sdlt += (value - 1500000) * 0.12;
    } else {
      // Investment: standard bands + 5% ADS on full value
      sdlt = value * 0.05;
      if (value > 125000) sdlt += Math.min(value - 125000, 125000) * 0.02;
      if (value > 250000) sdlt += Math.min(value - 250000, 675000) * 0.05;
      if (value > 925000) sdlt += Math.min(value - 925000, 575000) * 0.10;
      if (value > 1500000) sdlt += (value - 1500000) * 0.12;
    }
    // Non-resident surcharge: +2% of full value
    if (isForeignBuyer) sdlt += value * 0.02;
    return Math.round(sdlt);
  }

  autoServiceCharge(): string {
    const pv = parseFloat((this.PropertyValue + '').replace(/,/g, '')) || 0;
    const charge = pv < 360000 ? 3.5 * 500 : 3.5 * 750;
    return this.validation.amountWithComma(charge.toString());
  }

  next() {
    let flag = true;

    this.calcData.foreignbuyer = this.foreignbuyer || '0';
    if (!this.dataService.EmptyNullOrUndefined(this.stampDutyLandTax)) {
      this.calcData.stampDutyLandTax = parseFloat(this.stampDutyLandTax.replace(/,/g, '')) || 0;
    } else {
      flag = false;
    }
    if (!this.dataService.EmptyNullOrUndefined(this.loanOriginationFee)) {
      this.calcData.loanOriginationFee = parseFloat(this.loanOriginationFee.replace('%', '')) || 0;
    }
    if (!this.dataService.EmptyNullOrUndefined(this.letteingManagFee)) {
      this.calcData.letteingManagFee = parseFloat(this.letteingManagFee.replace('%', '')) || 0;
    } else {
      this.calcData.letteingManagFee = 0;
    }
    if (!this.dataService.EmptyNullOrUndefined(this.groundRent)) {
      this.calcData.groundRent = parseFloat(this.groundRent.replace(/,/g, '')) || 0;
    } else {
      this.calcData.groundRent = 0;
    }
    if (!this.dataService.EmptyNullOrUndefined(this.serviceCharges)) {
      this.calcData.serviceCharges = parseFloat(this.serviceCharges.replace(/,/g, '')) || 0;
    } else {
      this.calcData.serviceCharges = 0;
    }
    if (!this.dataService.EmptyNullOrUndefined(this.miscelleneousExpense)) {
      this.calcData.miscelleneousExpense = parseFloat(this.miscelleneousExpense.replace(/,/g, '')) || 0;
    } else {
      this.calcData.miscelleneousExpense = 0;
    }

    // Ensure defaults for result component
    if (this.dataService.EmptyNullOrUndefined(this.calcData.homecurrency)) {
      this.calcData.homecurrency = 1;
    }
    if (this.dataService.EmptyNullOrUndefined(this.calcData.fxgrowth)) {
      this.calcData.fxgrowth = 0;
    }

    if (flag) {
      this.calcData.reportSavedOnServer = false;
      localStorage.setItem('calcData', JSON.stringify(this.calcData));
      this.router.navigate(['/cashflow/result']);
    }
  }
}
